import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useChatbot } from '../../Context/ChatbotContext';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Avatar,
  Stack,
  Fab,
  Drawer,
  AppBar,
  Toolbar,
  CircularProgress,
  Chip,
  Button,
} from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  Person as UserIcon,
} from '@mui/icons-material';
import ChatbotIcon from '../Icons/ChatbotIcon';
import { useTranslation } from 'react-i18next';
import axios from '../../axiosConfig';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import { useTheme } from '@mui/material/styles';
import ChatMessageAttachments from './ChatMessageAttachments';
import { resolveMediaUrl } from '../../utils/mediaUrl';
import { isInternalAppPath } from '../../utils/chatbotMedia';
import { isModifiedClick, navigateWithinApp } from '../../utils/spaNavigation';
import { UserDetailsContext } from '../../Context/userDetailsContext';
import { ConfigLinksDetailsContext } from '../../Context/cofingLinksDetailsContext';
import { getAvatarInitials, getAvatarColor } from '../../utils/avatarUtils';

const HISTORY_LIMIT = 6; // Last 6 text turns (3 from user, 3 from bot)

const toArray = (value) => (Array.isArray(value) ? value : []);

const takeNextId = (ref) => {
  const id = ref.current;
  ref.current += 1;
  return id;
};

// react-markdown strips tel: links by default; keep phone numbers clickable.
const chatUrlTransform = (value) => (/^tel:/i.test(value) ? value : defaultUrlTransform(value));

const ChatMarkdownLink = ({ node, href, children, onInternalNavigate, ...props }) => {
  if (!href) {
    return <span {...props}>{children}</span>;
  }

  // Internal portal links render as buttons inside the chat.
  if (isInternalAppPath(href)) {
    return (
      <Button
        {...props}
        component="a"
        href={href}
        size="small"
        variant="contained"
        onClick={(event) => {
          if (isModifiedClick(event)) return;
          event.preventDefault();
          onInternalNavigate(href);
        }}
        sx={{
          mx: 0.5,
          my: 0.3,
          fontWeight: 700,
          borderRadius: 2,
          textTransform: 'none',
          fontSize: '0.8rem',
          boxShadow: 'none',
          '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.15)' },
        }}
      >
        {children}
      </Button>
    );
  }

  // Anything else would replace the page (and the in-memory chat), so open it in a new tab.
  const opensNewTab = !/^(mailto:|tel:|#)/i.test(href);
  return (
    <a {...props} href={href} {...(opensNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
      {children}
    </a>
  );
};

const Chatbot = ({ open, onClose, onOpen }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isArabic = (i18n.language || 'ar').startsWith('ar');
  const { userDetails } = React.useContext(UserDetailsContext);
  const { configLinks } = React.useContext(ConfigLinksDetailsContext);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const nextMessageIdRef = useRef(1);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const municipalityName = isArabic
    ? (configLinks?.municipalityNameAr || t('chatbot_defaultMunicipalityName'))
    : (configLinks?.municipalityNameEn || t('chatbot_defaultMunicipalityName'));

  const assistantName = t('chatbot_assistantName', { name: municipalityName });
  const welcomeText = t('chatbot_welcome', { assistant: assistantName });

  // Scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Also scroll to bottom when drawer opens
  useEffect(() => {
    if (open) {
      // small delay to ensure Drawer content is rendered
      const id = setTimeout(scrollToBottom, 150);
      return () => clearTimeout(id);
    }
  }, [open]);

  // Initialize with welcome message
  useEffect(() => {
    // Only show welcome message if we have the actual municipality name
    const hasRealMunicipalityName = Boolean(configLinks?.municipalityNameAr?.trim() || configLinks?.municipalityNameEn?.trim());

    if (messages.length === 0 && hasRealMunicipalityName) {
      setMessages([
        {
          id: takeNextId(nextMessageIdRef),
          text: welcomeText,
          sender: 'bot',
          timestamp: new Date(),
          isWelcome: true,
        },
      ]);
    }
  }, [welcomeText, configLinks, messages.length]);

  // The chatbot lives outside the router; close the drawer so the target page is visible.
  const handleInternalNavigate = useCallback((path) => {
    onCloseRef.current?.();
    navigateWithinApp(path);
  }, []);

  const markdownComponents = useMemo(() => ({
    a: (linkProps) => <ChatMarkdownLink {...linkProps} onInternalNavigate={handleInternalNavigate} />,
  }), [handleInternalNavigate]);

  const handleSendMessage = async () => {
    const text = inputMessage;
    if (!text.trim() || isLoading) return;

    const userMessage = {
      id: takeNextId(nextMessageIdRef),
      text,
      sender: 'user',
      timestamp: new Date(),
    };

    // History is text-only and skips the welcome message and error bubbles.
    const history = messages
      .filter((m) => !m.isWelcome && !m.isError && typeof m.text === 'string' && m.text.trim())
      .slice(-HISTORY_LIMIT)
      .map((m) => ({
        role: m.sender === 'user' ? 'user' : 'assistant',
        content: m.text,
      }));

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const clientNow = new Date();
      const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'West Bank Standard Time';
      // The axios interceptor already unwraps dataItem (ChatResponseDto).
      const payload = await axios.post('/Chatbot/chat', {
        message: text,
        history,
      }, {
        headers: {
          'X-Client-Sent-At': clientNow.toISOString(),
          'X-Client-TimeZone': clientTimeZone,
          'X-Client-Utc-Offset-Minutes': String(clientNow.getTimezoneOffset()),
        },
      });

      const responseText = payload?.response || payload?.Response || payload?.message;
      const botMessage = responseText
        ? {
            id: takeNextId(nextMessageIdRef),
            text: responseText,
            sender: 'bot',
            timestamp: new Date(),
            attachments: toArray(payload.attachments),
            links: toArray(payload.links),
            sources: toArray(payload.sources),
          }
        : {
            id: takeNextId(nextMessageIdRef),
            text: t('chatbot_noResponse'),
            sender: 'bot',
            timestamp: new Date(),
            isError: true,
          };
      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      console.error('Error sending message:', error);

      const errorData = error?.response?.data || error;
      const serverMessage = typeof errorData?.message === 'string' ? errorData.message : '';
      let errorText;

      if (errorData?.error) {
        // Rejections without a server body: network failure, timeout, or a 429 from the rate limiter
        errorText = /\b429\b/.test(serverMessage) ? t('chatbot_rateLimited') : t('chatbot_connectionError');
      } else if (serverMessage.includes('login')) {
        errorText = t('chatbot_loginRequired');
      } else if (serverMessage) {
        errorText = serverMessage;
      } else {
        errorText = t('chatbot_connectionError');
      }

      const errorMessage = {
        id: takeNextId(nextMessageIdRef),
        text: errorText,
        sender: 'bot',
        timestamp: new Date(),
        isError: true,
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const quickQuestions = [
    t('chatbot_quickQuestion1'),
    t('chatbot_quickQuestion2'),
    t('chatbot_quickQuestion3'),
    t('chatbot_quickQuestion4'),
  ];

  const handleQuickQuestion = (question) => {
    setInputMessage(question);
  };

  return (
    <Drawer
      anchor={'left'}
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          position: 'absolute',
          width: { xs: '100%', md: 420 },
          height: { xs: '70vh', md: '65vh' },
          boxShadow: `0 24px 48px ${theme.palette.primary.main}25`,
          top: 'auto',
          bottom: { xs: 0, md: 24 },
          left: { xs: 0, md: 90 },
          display: 'flex',
          flexDirection: 'column',
          borderRadius: { xs: '16px 16px 0 0', md: 7 },
          border: `1px solid ${theme.palette.primary.main}22`,
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ height: { xs: '70vh', md: '65vh' }, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <AppBar
          position="static"
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            boxShadow: 'none',
          }}
        >
          <Toolbar>
             <IconButton color="inherit" onClick={onClose} aria-label={t('chatbot_closeAssistant')}>
              <CloseIcon />
            </IconButton>

            <Typography variant="h6" sx={{ flexGrow: 1, textAlign: 'right',mx: 2 }}>
              {assistantName}
            </Typography>
               <Avatar sx={{ bgcolor: 'transparent', fontSize: 24 , borderRadius:10, border: '1.5px solid white' ,padding:"4px" }}>
              <ChatbotIcon />
            </Avatar>
          </Toolbar>
        </AppBar>

        {/* Quick Questions */}
        {messages.length <= 1 && (
          <Box
            sx={{
              p: 2,
              backgroundColor: `${theme.palette.primary.light}1A`,
              borderBottom: `1px solid ${theme.palette.primary.main}1F`,
              direction: isArabic ? 'rtl' : 'ltr',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                mb: 1,
                display: 'block',
                fontWeight: 'bold',
                textAlign: isArabic ? 'right' : 'left',
                direction: isArabic ? 'rtl' : 'ltr'
              }}
            >
              {t('chatbot_commonQuestions')}
            </Typography>
            <Stack
              direction={isArabic ? 'row-reverse' : 'row'}
              spacing={1}
              flexWrap="wrap"
              gap={1}
              sx={{
                justifyContent: isArabic ? 'flex-end' : 'flex-start',
                direction: isArabic ? 'rtl' : 'ltr'
              }}
            >
              {quickQuestions.map((question, index) => (
                <Chip
                  key={index}
                  label={question}
                  onClick={() => handleQuickQuestion(question)}
                  size="small"
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: `${theme.palette.primary.main}14`,
                    color: theme.palette.primary.dark,
                    border: `1px solid ${theme.palette.primary.main}22`,
                  }}
                />
              ))}
            </Stack>
          </Box>
        )}

        {/* Messages Area */}
        <Box
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            p: 2,
            background: `linear-gradient(180deg, ${theme.palette.primary.light}14 0%, ${theme.palette.background.default} 70%)`,
          }}
        >
          {messages.map((message) => (
            <Box
              key={message.id}
              sx={{
                display: 'flex',
                justifyContent: message.sender === 'user' ? (isArabic ? 'flex-start' : 'flex-end') : (isArabic ? 'flex-end' : 'flex-start'),
                mb: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: message.sender === 'user' ? (isArabic ? 'row' : 'row-reverse') : (isArabic ? 'row-reverse' : 'row'),
                  alignItems: 'flex-start',
                  maxWidth: '80%',
                }}
              >
                <Avatar
                  src={
                    message.sender === 'user' && userDetails?.isAuthenticated
                      ? (userDetails?.citizen?.profilePicturePreview ||
                        resolveMediaUrl(userDetails?.citizen?.profilePicture, { attachmentRoot: true }))
                      : undefined
                  }
                  sx={{
                    bgcolor: message.sender === 'user'
                      ? (userDetails?.isAuthenticated
                          ? getAvatarColor(`${userDetails?.citizen?.firstName} ${userDetails?.citizen?.familyName}`)
                          : 'primary.main'
                        )
                      : 'secondary.main',
                    width: 34,
                    height: 34,
                    mx: 1,
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '1.2rem',
                  }}
                >
                  {message.sender === 'user'
                    ? (userDetails?.isAuthenticated
                        ? (!(userDetails?.citizen?.profilePicture || userDetails?.citizen?.profilePicturePreview) &&
                            getAvatarInitials(`${userDetails?.citizen?.firstName} ${userDetails?.citizen?.familyName}`))
                        : <UserIcon fontSize="small" />
                      )
                    : <ChatbotIcon  />
                  }
                </Avatar>
                <Paper
                  sx={{
                    py: 0.25,
                    px: 1.5,
                    backgroundColor: message.sender === 'user' ? 'primary.main' : theme.palette.background.paper,
                    color: message.sender === 'user' ? 'white' : 'text.primary',
                    borderRadius: 4,
                    direction: isArabic ? 'rtl' : 'ltr',
                    textAlign: isArabic ? 'right' : 'left',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    maxWidth: '100%',
                    minWidth: 0,
                    border: message.sender === 'user' ? 'none' : `1px solid ${theme.palette.primary.main}1A`,
                    boxShadow: message.sender === 'user'
                      ? `0 8px 18px ${theme.palette.primary.main}36`
                      : `0 6px 14px ${theme.palette.primary.main}14`,
                  }}
                >
                  <Box
                    sx={{
                      '& p': { margin: '0.5em 0', direction: isArabic ? 'rtl' : 'ltr', textAlign: isArabic ? 'right' : 'left', wordWrap: 'break-word' },
                      '& ul, & ol': { paddingInlineStart: '1.5em', margin: '0.5em 0', direction: isArabic ? 'rtl' : 'ltr', textAlign: isArabic ? 'right' : 'left' },
                      '& li': { marginBottom: '0.25em', direction: isArabic ? 'rtl' : 'ltr', textAlign: isArabic ? 'right' : 'left', wordWrap: 'break-word' },
                      '& h1, & h2, & h3': { margin: '0.5em 0', fontWeight: 'bold', direction: isArabic ? 'rtl' : 'ltr', textAlign: isArabic ? 'right' : 'left', wordWrap: 'break-word' },
                      '& strong': { fontWeight: 'bold' },
                      '& a': {
                        color: message.sender === 'user' ? 'inherit' : theme.palette.primary.main,
                        textDecoration: 'underline',
                        overflowWrap: 'anywhere',
                      },
                      '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1 },
                      fontSize: '0.875rem',
                      lineHeight: 1.6,
                      direction: isArabic ? 'rtl' : 'ltr',
                      textAlign: isArabic ? 'right' : 'left',
                      fontFamily: isArabic ? '"Noto Sans Arabic", "Segoe UI", Roboto, sans-serif' : '"Segoe UI", Roboto, sans-serif',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                    }}
                  >
                    <ReactMarkdown components={markdownComponents} urlTransform={chatUrlTransform}>
                      {message.text}
                    </ReactMarkdown>
                  </Box>
                  {message.sender === 'bot' && (message.attachments?.length > 0 || message.links?.length > 0) && (
                    <ChatMessageAttachments
                      attachments={message.attachments}
                      links={message.links}
                      apiBaseUrl={axios.defaults.baseURL}
                      onInternalLinkClick={handleInternalNavigate}
                    />
                  )}
                </Paper>
              </Box>
            </Box>
          ))}

          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: isArabic ? 'flex-end' : 'flex-start', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: isArabic ? 'row-reverse' : 'row' }}>
                <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32, mx: 1 }}>
                  <ChatbotIcon fontSize="small" />
                </Avatar>
                <Paper
                  sx={{
                    p: 1.25,
                    borderRadius: 4,
                    border: `1px solid ${theme.palette.primary.main}25`,
                    boxShadow: `0 8px 16px ${theme.palette.primary.main}14`,
                    backgroundColor: `${theme.palette.primary.light}12`,
                  }}
                >
                  <Stack direction={isArabic ? 'row-reverse' : 'row'} spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        '& span': {
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          display: 'inline-block',
                          backgroundColor: theme.palette.primary.main,
                          opacity: 0.4,
                          animation: 'chatbotPulse 1.2s infinite ease-in-out',
                        },
                        '& span:nth-of-type(2)': { animationDelay: '0.2s' },
                        '& span:nth-of-type(3)': { animationDelay: '0.4s' },
                        '@keyframes chatbotPulse': {
                          '0%, 100%': { opacity: 0.35, transform: 'translateY(0)' },
                          '50%': { opacity: 1, transform: 'translateY(-2px)' },
                        },
                      }}
                    >
                      <span />
                      <span />
                      <span />
                    </Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                      {t('chatbot_thinking')}
                    </Typography>
                  </Stack>
                </Paper>
              </Box>
            </Box>
          )}

          <div ref={messagesEndRef} />
        </Box>

        {/* Input Area */}
        <Paper
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
            backgroundColor: `${theme.palette.background.paper}`,
          }}
        >
          <Stack direction="row" spacing={1}>
            <TextField
              fullWidth
              multiline
              maxRows={3}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t('chatbot_inputPlaceholder')}
              FormHelperTextProps={{
                sx: {
                  textAlign: isArabic ? 'right' : 'left',
                  direction: isArabic ? 'rtl' : 'ltr',
                }
              }}
              variant="outlined"
              size="small"
              dir={isArabic ? 'rtl' : 'ltr'}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 4,
                },
                '& .MuiInputBase-input': {
                  textAlign: isArabic ? 'right' : 'left',
                  direction: isArabic ? 'rtl' : 'ltr',
                },
              }}
            />
            <IconButton color="primary" onClick={handleSendMessage} disabled={isLoading} aria-label={t('chatbot_send')}>
              {isLoading ? <CircularProgress size={24} /> : <SendIcon />}
            </IconButton>
          </Stack>
        </Paper>
      </Box>
    </Drawer>
  );
};

// Floating Button Component
export const ChatbotButton = () => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const { chatbotSettings } = useChatbot();

  // Broadcast open state changes so other floating UI can react (e.g., hide when chatbot is open)
  useEffect(() => {
    const event = new CustomEvent('chatbot-open-changed', { detail: { open } });
    window.dispatchEvent(event);
  }, [open]);

  // Don't render the button if chatbot is disabled
  if (!chatbotSettings.enabled) {
    return null;
  }

  return (
    <>
      <Fab
        color="primary"
        aria-label={t('chatbot_openAssistant')}
        onClick={() => setOpen(true)}
        sx={{
          position: 'fixed',
          bottom: 22,
          left: 20,
          zIndex: 1000,
          backgroundColor: 'primary.main',
          color: 'white',
          '&:hover': {
            backgroundColor: 'primary.dark',
            transform: 'scale(1.1)',
          },
          '&:active': {
            transform: 'scale(0.95)',
          },
        }}
      >
        <ChatbotIcon sx={{ fontSize: 28 }} />
      </Fab>
      <Chatbot open={open} onClose={() => setOpen(false)} onOpen={() => setOpen(true)} />
    </>
  );
};

export default Chatbot;
